import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { CurrentUserService } from "../services/current-user.service";
import { CurrentAccountService } from "../services/current-account.service";
import { AuthTokenService } from '../services/auth-token.service';
import { AuthService } from '../services/auth.service';
import { ActivatedRoute } from '@angular/router';
import { AcceptDpaGQL } from '../graphql/gql';
import { lastValueFrom } from 'rxjs';
import { appConstants } from '../app-constants';

/**
 * GdprAcceptanceComponent is a component that handles the GDPR acceptance functionality for the application.
 *
 */
@Component({
  selector: 'app-gdpr-acceptance',
  templateUrl: './gdpr_acceptance.component.html',
  styleUrls: ['./gdpr_acceptance.component.css'],
  standalone: false
})
export class GdprAcceptanceComponent implements OnInit {
  form!: FormGroup;
  submitted = false;
  private returnUrl: string | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private currentAccountService: CurrentAccountService,
    private currentUserService: CurrentUserService,
    private authTokenService: AuthTokenService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private acceptDpaGQL: AcceptDpaGQL
  ) {}

  ngOnInit() {
    this.form = this.formBuilder.group({
      accept: ['', Validators.required],
    });

    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
  }

  /**
   * Submits the form data and handles the response from the API.
   *
   * @async
   * @function onAccept
   * @returns {Promise} A Promise that resolves when the API responds with data.
   */
  async onAccept(): Promise<any> {
    try {
      const result = await lastValueFrom(this.acceptDpaGQL.mutate());
      if (result?.data?.acceptDpa?.user) {
        this.authService.completeLoginAndRedirect(this.returnUrl);
      }
    } catch (error) {
      console.error('Error accepting DPA:', error);
      // Handle error as needed
    }
  }

  async onDecline(): Promise<any> {
    const confirmed = confirm('If you decline the Data Processing Agreement, you will be logged out. Do you want to continue?');
    
    if (confirmed) {
      try {
        // Perform logout by calling the legacy app's sign out endpoint
        await fetch(environment.legacyAppUrl + "/users/sign_out", {
          "headers": {
            "accept": "application/json",
            "content-type": "application/x-www-form-urlencoded",
            "X-CSRF-Token": await this.authTokenService.getAuthenticityToken()
          },
          "method": "DELETE",
          "credentials": 'include'
        });
        
        // Clear local user state
        this.currentUserService.logout();
        this.authTokenService.clear();
        
        // Redirect to login page
        await this.router.navigateByUrl("/login");
      } catch (error) {
        console.error('Error during logout:', error);
        // Even if logout fails, clear local state and redirect
        this.currentUserService.logout();
        this.authTokenService.clear();
        await this.router.navigateByUrl("/login");
      }
    }
  }

  logoUrl(): string {
    return appConstants.LOGO_URL;
  }
}
